import React, { useState, useCallback, useEffect } from 'react';
import { ethers } from 'ethers';
import WalletConnect from './WalletConnect';
import QRScanner from './QRScanner';
import PaymentDetails from './PaymentDetails';
import ChainSelector from './ChainSelector';
import TokenBalanceFetcher from './TokenBalanceFetcher';
import { scanQR, selectSourceChain, executePayment, checkPayment } from '../../services/paymentApi';
import { usePaymentTimer, formatTime } from '../../hooks/usePaymentTimer';

import NoxPayLogo from '../../NoxPayLogo.svg';
import HeaderInstallButton from '../../HeaderInstallButton';

import { useReownWallet } from '../../hooks/useReownWallet';

const FLOW_STATES = {
  CONNECT_WALLET: 'CONNECT_WALLET',
  SCAN_QR: 'SCAN_QR',
  PROCESSING_QR: 'PROCESSING_QR',
  DISPLAY_DETAILS: 'DISPLAY_DETAILS',
  SELECT_CHAIN: 'SELECT_CHAIN',
  FETCH_BALANCES: 'FETCH_BALANCES',
  SELECT_PAYMENT: 'SELECT_PAYMENT',
  SIGNING: 'SIGNING',
  POLLING: 'POLLING',
  CONFIRMED: 'CONFIRMED',
  ERROR: 'ERROR'
};

const PaymentTerminal = () => {
  // Use Reown wallet hook

const { 
    address: walletAddress, 
    chainId: currentChainId, 
    isConnected,
    provider
  } = useReownWallet();
  

  const [flowState, setFlowState] = useState(FLOW_STATES.CONNECT_WALLET);
  const [paymentData, setPaymentData] = useState(null);
  const [selectedChain, setSelectedChain] = useState(null);
  const [tokenBalances, setTokenBalances] = useState(null);
  const [paymentOptions, setPaymentOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState(null);
  const [signedPayment, setSignedPayment] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [relayedPayment, setRelayedPayment] = useState(null);
  const [confirmedPayment, setConfirmedPayment] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const timeRemaining = usePaymentTimer(paymentData?.expiresAt);

  // Auto-advance to QR scan when wallet connects
  useEffect(() => {
    if (isConnected && walletAddress && currentChainId && flowState === FLOW_STATES.CONNECT_WALLET) {
      setFlowState(FLOW_STATES.SCAN_QR);
    }
  }, [isConnected, walletAddress, currentChainId, flowState]);

  // Polling effect (unchanged)
  useEffect(() => {
    if (flowState !== FLOW_STATES.POLLING) return;
    
    let pollInterval;
    let pollTimeout;
    
    const startPolling = async () => {
      console.log('🔄 Starting payment polling...');
      
      const poll = async () => {
        try {
          const status = await checkPayment(paymentData.txRef);
          setPollCount(prev => prev + 1);
          console.log('📊 Payment status:', status.state);
          
          if (status.state === 'CONFIRMED') {
            console.log('✅ Payment confirmed!');
            clearInterval(pollInterval);
            clearTimeout(pollTimeout);
            setConfirmedPayment(status);
            setFlowState(FLOW_STATES.CONFIRMED);
          } else if (status.state === 'CANCELLED' || status.state === 'EXPIRED') {
            console.log('❌ Payment failed:', status.state);
            clearInterval(pollInterval);
            clearTimeout(pollTimeout);
            setError(`Payment ${status.state.toLowerCase()}: ${status.cancelReason || 'Unknown reason'}`);
            setFlowState(FLOW_STATES.ERROR);
          }
        } catch (err) {
          console.error('❌ Polling error:', err);
        }
      };
      
      await poll();
      pollInterval = setInterval(poll, 3000);
      pollTimeout = setTimeout(() => {
        clearInterval(pollInterval);
        setError('Payment confirmation timeout. Please check your transaction history.');
        setFlowState(FLOW_STATES.ERROR);
      }, 5 * 60 * 1000);
    };
    
    startPolling();
    
    return () => {
      if (pollInterval) clearInterval(pollInterval);
      if (pollTimeout) clearTimeout(pollTimeout);
    };
  }, [flowState, paymentData?.txRef]);


// Reset flow when wallet disconnects
useEffect(() => {
  if (!isConnected && flowState !== FLOW_STATES.CONNECT_WALLET) {
    setPaymentData(null);
    setSelectedChain(null);
    setTokenBalances(null);
    setPaymentOptions([]);
    setSelectedOption(null);
    setSignedPayment(null);
    setError(null);
    setFlowState(FLOW_STATES.CONNECT_WALLET);
  }
}, [isConnected, flowState]);
  //}, [isConnected, manualDisconnect, flowState]);





  const handleWalletConnected = useCallback((walletInfo) => {

    if (flowState === FLOW_STATES.CONNECT_WALLET) {
      setFlowState(FLOW_STATES.SCAN_QR);
    }
  }, [flowState]);



  const handleQRScanned = async (txRef) => {
    console.log('🔍 QR Scanned, processing:', txRef);
    setFlowState(FLOW_STATES.PROCESSING_QR);
    setError(null);
    
    try {
      const response = await scanQR(txRef);
      
      if (response.valid) {
        setPaymentData(response.payment);
        await new Promise(resolve => setTimeout(resolve, 2000));
        setFlowState(FLOW_STATES.DISPLAY_DETAILS);
      } else {
        let errorMessage;
        switch (response.reason) {
          case 'not_found':
            errorMessage = 'Payment not found. This QR code may have expired or been deleted. Please request a new payment QR code from the merchant.';
            break;
          case 'already_scanned':
            errorMessage = 'This QR code has already been scanned. Please continue with the existing payment or request a new QR code.';
            break;
          case 'expired':
            errorMessage = 'This payment has expired. Please request a new payment QR code from the merchant.';
            break;
          case 'source_selected':
            errorMessage = 'Payment is already in progress. Please continue with the existing payment flow.';
            break;
          case 'paid':
            errorMessage = 'This payment has already been completed. Please request a new payment QR code if needed.';
            break;
          case 'invalid_state':
            errorMessage = 'Payment is in an invalid state. Please request a new payment QR code.';
            break;
          default:
            errorMessage = response.message || 'Invalid QR code. Please try scanning again or request a new payment QR code.';
        }
        setError(errorMessage);
        setFlowState(FLOW_STATES.ERROR);
      }
    } catch (err) {
      console.error('❌ QR scan error:', err);
      setError(err.message || 'Failed to process QR code. Please try again.');
      setFlowState(FLOW_STATES.ERROR);
    }
  };

  const handleQRError = (err) => setError(err.message);
  const handleStartChainSelection = () => setFlowState(FLOW_STATES.SELECT_CHAIN);



const handleChainConfirmed = (chain, verifiedChainId) => {
  console.log('✅ Chain confirmed:', { chain: chain.name, chainId: verifiedChainId });
  
  // Verify the chain ID matches what we expect
  if (Number(verifiedChainId) !== Number(chain.chainId)) {
    console.error('⚠️ Chain ID mismatch!', { 
      expected: chain.chainId, 
      actual: verifiedChainId 
    });
    setError(`Chain mismatch. Please ensure you're on ${chain.name} (Chain ID: ${chain.chainId})`);
    setFlowState(FLOW_STATES.SELECT_CHAIN);
    return;
  }
  
  setSelectedChain(chain);
  setFlowState(FLOW_STATES.FETCH_BALANCES);
};


/*
  const handleChainConfirmed = (chain, verifiedChainId) => {
    console.log('✅ Chain confirmed:', { chain: chain.name, chainId: verifiedChainId });
    setSelectedChain(chain);
    setFlowState(FLOW_STATES.FETCH_BALANCES);
  };
*/




const handleBalancesFetched = async (data) => {
  if (!data.balances || data.balances.length === 0) {
    setError("No token balances available.");
    setFlowState(FLOW_STATES.ERROR);
    return; 
  }
  
  // Verify we're still on the correct chain
  if (Number(currentChainId) !== Number(selectedChain.chainId)) {
    setError(`You're no longer on ${selectedChain.name}. Please switch back to continue.`);
    setFlowState(FLOW_STATES.SELECT_CHAIN);
    return;
  }
  
  setTokenBalances(data.balances);
  setIsLoading(true);
  setError(null);

  try {
    const result = await selectSourceChain(
      paymentData.txRef,
      selectedChain.chainId,
      walletAddress,
      data.balances
    );
    
    if (!result.success) {
      let errorMessage;
      switch (result.code) {
        case 'PAYMENT_NOT_FOUND':
          errorMessage = 'Payment not found. The payment may have expired. Please start a new payment.';
          break;
        case 'INVALID_STATE':
          if (result.reason === 'not_scanned') {
            errorMessage = 'Payment not yet scanned. Please scan the QR code first.';
          } else if (result.reason === 'already_selected') {
            errorMessage = 'Source chain already selected. Please continue with payment.';
          } else if (result.reason === 'expired') {
            errorMessage = 'Payment has expired. Please request a new payment QR code.';
          } else if (result.reason === 'payment_in_progress') {
            errorMessage = 'Payment is already being processed. Please wait or start a new payment.';
          } else {
            errorMessage = result.error || 'Payment is in an invalid state. Please start a new payment.';
          }
          break;
        case 'NO_VALID_ROUTES':
          errorMessage = 'No valid payment routes found. Please ensure you have enough balance to cover the payment and gas fees, or try a different network.';
          break;
        default:
          errorMessage = result.error || 'Failed to get payment options. Please try again.';
      }
      throw new Error(errorMessage);
    }
  
    if (result.payment?.expiresAt) {
      setPaymentData(prev => ({ ...prev, expiresAt: result.payment.expiresAt }));
    }
  
    setPaymentOptions(result.options);
    setFlowState(FLOW_STATES.SELECT_PAYMENT);
  } catch (err) {
    console.error('❌ Select source chain error:', err);
    setError(err.message || 'Failed to get payment options');
    setFlowState(FLOW_STATES.ERROR);
  } finally {
    setIsLoading(false);
  }
};



/*
  const handleBalancesFetched = async (data) => {
    if (!data.balances || data.balances.length === 0) {
      setError("No token balances available.");
      setFlowState(FLOW_STATES.ERROR);
      return; 
    }
    
    setTokenBalances(data.balances);
    setIsLoading(true);
    setError(null);

    try {
      const result = await selectSourceChain(
        paymentData.txRef,
        selectedChain.chainId,
        walletAddress,
        data.balances
      );
      
      if (!result.success) {
        let errorMessage;
        switch (result.code) {
          case 'PAYMENT_NOT_FOUND':
            errorMessage = 'Payment not found. The payment may have expired. Please start a new payment.';
            break;
          case 'INVALID_STATE':
            if (result.reason === 'not_scanned') {
              errorMessage = 'Payment not yet scanned. Please scan the QR code first.';
            } else if (result.reason === 'already_selected') {
              errorMessage = 'Source chain already selected. Please continue with payment.';
            } else if (result.reason === 'expired') {
              errorMessage = 'Payment has expired. Please request a new payment QR code.';
            } else if (result.reason === 'payment_in_progress') {
              errorMessage = 'Payment is already being processed. Please wait or start a new payment.';
            } else {
              errorMessage = result.error || 'Payment is in an invalid state. Please start a new payment.';
            }
            break;
          case 'NO_VALID_ROUTES':
            errorMessage = 'No valid payment routes found. Please ensure you have enough balance to cover the payment and gas fees, or try a different network.';
            break;
          default:
            errorMessage = result.error || 'Failed to get payment options. Please try again.';
        }
        throw new Error(errorMessage);
      }
    
      if (result.payment?.expiresAt) {
        setPaymentData(prev => ({ ...prev, expiresAt: result.payment.expiresAt }));
      }
    
      setPaymentOptions(result.options);
      setFlowState(FLOW_STATES.SELECT_PAYMENT);
    } catch (err) {
      console.error('❌ Select source chain error:', err);
      setError(err.message || 'Failed to get payment options');
      setFlowState(FLOW_STATES.ERROR);
    } finally {
      setIsLoading(false);
    }
  };
*/



const handlePayWithOption = async (option, optionIndex) => {
  setSelectedOption({ ...option, optionIndex });
  setFlowState(FLOW_STATES.SIGNING);
  setError(null);
  
  try {
    console.log('🖊️ Signing payment with option:', option);
    
    if (!provider) {
      throw new Error('Wallet provider not available. Please reconnect your wallet.');
    }

    // Get signer from the ethers provider
    const signer = provider.getSigner();
    
    console.log('📝 Requesting signature...');
    
    // Sign the typed data using EIP-712
    const signature = await signer._signTypedData(
      option.permit2Payload.domain,
      option.permit2Payload.types,
      option.permit2Payload.message
    );
    
    console.log('✅ Signature obtained:', signature.substring(0, 20) + '...');
    setIsLoading(true);
    
    const result = await executePayment(
      paymentData.txRef,
      optionIndex,
      signature,
      option.permit2Payload
    );
    
    if (!result.success) {
      let errorMessage;
      switch (result.code) {
        case 'PAYMENT_NOT_FOUND':
          errorMessage = 'Payment not found. The payment may have expired. Please start a new payment.';
          break;
        case 'INVALID_STATE':
          if (result.reason === 'already_signed') {
            errorMessage = 'Payment already signed and being processed in the background. Please wait for confirmation.';
          } else if (result.reason === 'expired') {
            errorMessage = 'Payment has expired. Please request a new payment QR code.';
          } else {
            errorMessage = result.error || 'Payment is in an invalid state. Please start a new payment.';
          }
          break;
        case 'INVALID_SIGNATURE':
          errorMessage = 'Invalid signature. Please try signing again.';
          break;
        default:
          errorMessage = result.error || result.details || 'Failed to execute payment. Please try again.';
      }
      throw new Error(errorMessage);
    }
    
    console.log('✅ Payment signed and relayed:', result);
    
    if (result.payment.state === 'RELAYED') {
      setRelayedPayment(result.payment);
      setPollCount(0);
      setFlowState(FLOW_STATES.POLLING);
    } else if (result.payment.state === 'CANCELLED') {
      throw new Error(result.payment?.cancelReason || 'Payment was cancelled');
    } else {
      throw new Error(`Unexpected payment state: ${result.payment?.state || result.state}`);
    }
  } catch (err) {
    console.error('❌ Payment execution error:', err);
    
    // Handle user rejection
    if (err.code === 4001 || err.code === 'ACTION_REJECTED') {
      setError('Signature rejected. Please try again.');
    } else if (err.message?.includes('user rejected')) {
      setError('Signature rejected. Please try again.');
    } else {
      setError(err.message || 'Failed to execute payment');
    }
    
    setFlowState(FLOW_STATES.SELECT_PAYMENT);
  } finally {
    setIsLoading(false);
  }
};



  /*
  const handlePayWithOption = async (option, optionIndex) => {
    setSelectedOption({ ...option, optionIndex });
    setFlowState(FLOW_STATES.SIGNING);
    setError(null);
    
    try {
      console.log('🖊️ Signing payment with option:', option);
      
      if (!provider) {
        throw new Error('Wallet provider not available');
      }

      const signer = await provider.getSigner();
      
      const signature = await signer._signTypedData(
        option.permit2Payload.domain,
        option.permit2Payload.types,
        option.permit2Payload.message
      );
      
      console.log('✅ Signature obtained:', signature.substring(0, 20) + '...');
      setIsLoading(true);
      
      const result = await executePayment(
        paymentData.txRef,
        optionIndex,
        signature,
        option.permit2Payload
      );
      
      if (!result.success) {
        let errorMessage;
        switch (result.code) {
          case 'PAYMENT_NOT_FOUND':
            errorMessage = 'Payment not found. The payment may have expired. Please start a new payment.';
            break;
          case 'INVALID_STATE':
            if (result.reason === 'already_signed') {
              errorMessage = 'Payment already signed and being processed in the background. Please wait for confirmation.';
            } else if (result.reason === 'expired') {
              errorMessage = 'Payment has expired. Please request a new payment QR code.';
            } else {
              errorMessage = result.error || 'Payment is in an invalid state. Please start a new payment.';
            }
            break;
          case 'INVALID_SIGNATURE':
            errorMessage = 'Invalid signature. Please try signing again.';
            break;
          default:
            errorMessage = result.error || result.details || 'Failed to execute payment. Please try again.';
        }
        throw new Error(errorMessage);
      }
      
      console.log('✅ Payment signed and relayed:', result);
      
      if (result.payment.state === 'RELAYED') {
        setRelayedPayment(result.payment);
        setPollCount(0);
        setFlowState(FLOW_STATES.POLLING);
      } else if (result.state === 'CANCELLED') {
        throw new Error(result.payment?.cancelReason || 'Payment was cancelled');
      } else {
        throw new Error(`Unexpected payment state: ${result.payment?.state || result.state}`);
      }
    } catch (err) {
      console.error('❌ Payment execution error:', err);
      
      if (err.code === 4001 || err.code === 'ACTION_REJECTED') {
        setError('Signature rejected. Please try again.');
      } else {
        setError(err.message || 'Failed to execute payment');
      }
      
      setFlowState(FLOW_STATES.SELECT_PAYMENT);
    } finally {
      setIsLoading(false);
    }
  };
*/


  const handleReset = () => {
    setFlowState(FLOW_STATES.SCAN_QR);
    setPaymentData(null);
    setSelectedChain(null);
    setTokenBalances(null);
    setPaymentOptions([]);
    setSelectedOption(null);
    setSignedPayment(null);
    setError(null);
  };

  return (
    <div className="payment-terminal">
      <div className="terminal-header">
        <div className="header-top">
          <div className="header-titles">
            <h1 className="terminal-title">
              <img src={NoxPayLogo} alt="NOX Logo" width={120} height={40}/>
            </h1>

{isConnected && <w3m-button />}

          </div>

          {(flowState === FLOW_STATES.CONNECT_WALLET || flowState === FLOW_STATES.SCAN_QR) && (
            <div className="wallet-header">
              <span className="wallet-address">
                <div style={{ marginRight: '10px', display: 'inline-block' }}>
                  <HeaderInstallButton lang={1} />
                </div>





              </span>
            </div>
          )}
        </div>
      </div>



      <div className="terminal-content">

        {flowState === FLOW_STATES.CONNECT_WALLET && (
          <WalletConnect onWalletConnected={handleWalletConnected} />
        )}

        {flowState === FLOW_STATES.SCAN_QR && (
          <QRScanner onScanSuccess={handleQRScanned} onScanError={handleQRError} />
        )}

        {flowState === FLOW_STATES.PROCESSING_QR && (
          <div className="terminal-section" style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div className="spinner" style={{ margin: '0 auto 20px' }} />
            <h2 className="section-title">Processing Payment QR Code</h2>
            <p style={{ color: '#666', marginTop: '16px', fontSize: '14px' }}>
              Validating payment details...
            </p>
          </div>
        )}

        {flowState === FLOW_STATES.DISPLAY_DETAILS && paymentData && (
          <PaymentDetails 
            paymentData={paymentData} 
            currentChainId={currentChainId}
            walletAddress={walletAddress}
            onSelectChain={handleStartChainSelection} 
            onCancel={handleReset} 
          />
        )}

        {flowState === FLOW_STATES.SELECT_CHAIN && paymentData && (
          <ChainSelector 
            availableChains={paymentData.availableChains}
            currentChainId={currentChainId}
            paymentData={paymentData}
            onChainConfirmed={handleChainConfirmed} 
            onCancel={() => setFlowState(FLOW_STATES.DISPLAY_DETAILS)} 
          />
        )}

        {flowState === FLOW_STATES.FETCH_BALANCES && selectedChain && (
          <TokenBalanceFetcher 
            selectedChain={selectedChain} 
            walletAddress={walletAddress} 
            paymentData={paymentData} 
            onBalancesFetched={handleBalancesFetched}
            onError={setError}
            onCancel={() => setFlowState(FLOW_STATES.SELECT_CHAIN)}
          />
        )}

        {/* SELECT_PAYMENT, SIGNING, POLLING, CONFIRMED, ERROR states remain the same as your original code */}


   {flowState === FLOW_STATES.SELECT_PAYMENT && (
          <div className="terminal-section" style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div className={timeRemaining < 30000 ? 'timer timer-warning' : 'timer'}>
              <span style={{ marginRight: '8px' }}>⏰</span>
              <span>Time Remaining: </span>
              <span className="timer-value">{formatTime(timeRemaining)}</span>
            </div>

            <h2 className="section-title">Select Payment Token</h2>
            <p className="section-description" style={{ marginBottom: '20px', color: '#666', textAlign: 'center' }}>
              Choose how you want to pay. Click "Pay Now" to sign and submit.
            </p>
            
            <div className="options-grid">
              {paymentOptions.map((opt, index) => {
                const tokenDecimals = opt.tokenInDecimals || 6;
                const merchantDecimals = opt.merchantTokenDecimals || 6;
                const fullAmount = parseFloat(opt.amountIn) / Math.pow(10, tokenDecimals);
                const merchantAmount = parseFloat(opt.merchantReceives) / Math.pow(10, merchantDecimals);
                
                const isDirect = opt.type === 'DIRECT';
                
                // For SWAP: calculate fees differently
                let feeAmount;
                let feeDisplay;
                
                if (isDirect) {
                  feeAmount = fullAmount - merchantAmount;
                  feeDisplay = `${feeAmount.toFixed(6)} ${opt.tokenIn}`;
                } else {
                  // SWAP: minBuyAmount is what we guarantee to get
                  const minBuyDecimals = opt.merchantTokenDecimals || 6;
                  const minBuyAmount = parseFloat(opt.minBuyAmount) / Math.pow(10, minBuyDecimals);
                  const swapFee = minBuyAmount - merchantAmount;
                  feeDisplay = `${swapFee.toFixed(6)} ${opt.merchantToken}`;
                }
                
                const displayDecimals = tokenDecimals >= 18 ? 8 : 6;

                return (
                  <div key={opt.tokenInAddress} className="option-card payment-card">
                    <div className="card-header">
                      <span className="token-name">{opt.tokenIn}</span>
                      <span className={`type-badge ${isDirect ? 'direct' : 'swap'}`}>
                        {isDirect ? '✓ DIRECT' : '⇄ SWAP'}
                      </span>
                    </div>
                    
                    <div className="amount-display">
                      {fullAmount.toFixed(displayDecimals)} <span className="currency">{opt.tokenIn}</span>
                    </div>
                    
                    <div className="fee-breakdown-box">
                      <div className="fee-row">
                        <span>Merchant receives:</span>
                        <span>{merchantAmount.toFixed(displayDecimals)} {opt.merchantToken}</span>
                      </div>
                      <div className="fee-row fee-highlight">
                        <span>{isDirect ? 'Gas Fee:' : 'Total Fees (Swap + Gas):'}</span>
                        <span>{feeDisplay}</span>
                      </div>
                      {!isDirect && (
                        <div className="fee-row" style={{ fontSize: '0.9em', color: '#666' }}>
                          <span>Min. guaranteed output:</span>
                          <span>{opt.minBuyAmountFormatted} {opt.merchantToken}</span>
                        </div>
                      )}
                    </div>

                    {isDirect ? (
                      <div className="status-indicator success">
                        <strong>✓ Ready to Sign</strong><br/>
                        Exact amount, no swaps needed
                      </div>
                    ) : (
                      <div className="status-indicator success">
                        <strong>✓ Ready to Sign</strong><br/>
                        Swap quote locked and ready
                      </div>
                    )}

                    <button
                      onClick={() => handlePayWithOption(opt, index)}
                      className="btn btn-primary btn-lg"
                      style={{ width: '100%', marginTop: '12px' }}
                      disabled={timeRemaining === 0}
                    >
                      Pay Now
                    </button>
                  </div>
                );
              })}
            </div>

            <div className="payment-info-footer">
              <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>ℹ️ About Payment Options:</div>
              <ul style={{ margin: '8px 0', paddingLeft: '20px', lineHeight: '1.6' }}>
                <li><strong>DIRECT payments:</strong> Pay directly in merchant's token. Amounts are exact and final.</li>
                <li><strong>SWAP payments:</strong> We'll convert your tokens to merchant's token. Quote is locked before signing.</li>
                <li>All fees (gas + swap) are included in the displayed amount.</li>
                <li>SWAP payments guarantee minimum output amount with 1.5% slippage protection.</li>
              </ul>
            </div>
            
            <button onClick={handleReset} className="btn btn-secondary" style={{ marginTop: '24px', width: '100%' }}>
              ← Cancel Payment
            </button>
          </div>
        )}




          {flowState === FLOW_STATES.SIGNING && (
          <div className="terminal-section" style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div className="spinner" style={{ margin: '0 auto 20px' }} />
            <h2 className="section-title">Waiting for Signature</h2>
            <p style={{ color: '#666', marginTop: '16px' }}>
              Please check your wallet and approve the signature request.
            </p>
            <p style={{ color: '#999', fontSize: '14px', marginTop: '16px' }}>
              After signing, your payment will be automatically submitted to the blockchain.
            </p>
            {selectedOption && (
              <div className="info-card" style={{ marginTop: '24px', maxWidth: '400px', margin: '24px auto 0' }}>
                <div className="info-row">
                  <span className="info-label">Payment Type:</span>
                  <span className="info-value">{selectedOption.type}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Paying with:</span>
                  <span className="info-value">{selectedOption.tokenIn}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Amount:</span>
                  <span className="info-value">{selectedOption.amountInFormatted}</span>
                </div>
                {selectedOption.type === 'SWAP' && (
                  <div className="info-row">
                    <span className="info-label">Min. Output:</span>
                    <span className="info-value">{selectedOption.minBuyAmountFormatted} {selectedOption.merchantToken}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}



{/* ✅ POLLING state - goes here directly after signing */}
        {flowState === FLOW_STATES.POLLING && relayedPayment && (
          <div className="terminal-section" style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div className="spinner" style={{ margin: '0 auto 20px' }} />
            <h2 className="section-title">Confirming Payment</h2>
            <p style={{ color: '#666', marginTop: '16px', marginBottom: '24px' }}>
              Your payment has been submitted to the blockchain. Waiting for confirmation...
            </p>
            
            <div className="info-card" style={{ maxWidth: '500px', margin: '0 auto' }}>
              <div className="info-row">
                <span className="info-label">Status:</span>
                <span className="info-value" style={{ color: '#ff9800', fontWeight: 'bold' }}>
                  RELAYED
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Task ID:</span>
                <span className="info-value" style={{ fontSize: '12px', fontFamily: 'monospace' }}>
                  {relayedPayment.gelatoTaskId?.substring(0, 20)}...
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Payment Type:</span>
                <span className="info-value">{relayedPayment.selectedOption?.type}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Token:</span>
                <span className="info-value">{relayedPayment.selectedOption?.tokenIn}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Poll Count:</span>
                <span className="info-value">{pollCount}</span>
              </div>
              {relayedPayment.isFakeRelay && (
                <div style={{ 
                  marginTop: '16px', 
                  padding: '12px', 
                  background: '#fff3cd', 
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#856404'
                }}>
                  🎭 <strong>Test Mode:</strong> Using simulated Gelato relay.<br/>
                  Payment will confirm in ~45 seconds.
                </div>
              )}
            </div>

            <p style={{ color: '#999', fontSize: '14px', marginTop: '24px' }}>
              This usually takes 30-60 seconds. Please don't close this window.
            </p>
          </div>
        )}

        {/* ✅ CONFIRMED state - unchanged */}
        {flowState === FLOW_STATES.CONFIRMED && confirmedPayment && (
          <div className="terminal-section" style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>🎉</div>
            <h2 className="section-title" style={{ color: '#4caf50' }}>Payment Confirmed!</h2>
            <p style={{ color: '#666', marginTop: '16px', marginBottom: '24px' }}>
              Your payment has been successfully confirmed on the blockchain.
            </p>
            
            <div className="info-card" style={{ maxWidth: '500px', margin: '0 auto 24px' }}>
              <div className="info-row">
                <span className="info-label">Status:</span>
                <span className="info-value" style={{ color: '#4caf50', fontWeight: 'bold' }}>
                  CONFIRMED ✓
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Transaction Hash:</span>
                <span className="info-value" style={{ fontSize: '12px', fontFamily: 'monospace', wordBreak: 'break-all' }}>
                  {confirmedPayment.txHash}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Block Number:</span>
                <span className="info-value">{confirmedPayment.blockNumber}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Confirmed At:</span>
                <span className="info-value">
                  {new Date(confirmedPayment.confirmedAt).toLocaleString()}
                </span>
              </div>
            </div>

            <button 
              onClick={handleReset} 
              className="btn btn-primary btn-lg"
            >
              Make Another Payment
            </button>
          </div>
        )}


        

        {flowState === FLOW_STATES.SIGNED && signedPayment && (
          <div className="terminal-section" style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '64px', marginBottom: '20px' }}>✅</div>
            <h2 className="section-title">Payment Signed!</h2>
            <p style={{ color: '#666', marginTop: '16px', marginBottom: '24px' }}>
              Your signature has been validated and stored.
            </p>
            
            <div className="info-card" style={{ maxWidth: '500px', margin: '0 auto' }}>
            
              <div className="info-row">
                <span className="info-label">Status:</span>
                <span className="info-value" style={{ color: '#4caf50', fontWeight: 'bold' }}>
                  {signedPayment.state}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Payment Type:</span>
                <span className="info-value">{signedPayment.selectedOption?.type}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Token:</span>
                <span className="info-value">{signedPayment.selectedOption?.tokenIn}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Amount:</span>
                <span className="info-value info-highlight">
                  {signedPayment.selectedOption?.amountIn}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Merchant Receives:</span>
                <span className="info-value">
                  {signedPayment.selectedOption?.merchantReceives}
                </span>
              </div>
            </div>

            
            <button 
              onClick={handleReset} 
              className="btn btn-primary btn-lg"
              style={{ marginTop: '32px' }}
            >
              Make Another Payment
            </button>
          </div>
        )}







        {/* I'm omitting them here to save space - they don't need changes */}

        {flowState === FLOW_STATES.ERROR && error && (
          <div className="terminal-section" style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div className="alert">
              <span>⚠️</span>
              <span>{error}</span>
            </div>
            <button onClick={handleReset} className="btn btn-primary btn-lg">
              Try Again
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentTerminal;